import {useDispatch, useSelector} from "react-redux"; // Import hooks from React-Redux to dispatch actions and access the store
import {useTranslation} from "react-i18next"; // Import the useTranslation hook for handling multi-language support
import {useEffect, useState} from "react"; // Import React hooks for managing side effects and local state
import {
    GetContractorRestriction, GetSupplierRestriction, UpdateContractorRestriction, UpdateSupplierRestriction
} from "../../../../reduxToolkit/restrictions/RestrictionApi"; // Import API functions for getting and updating contractor and supplier restrictions
import {supplierFile} from "../../../../constant/userType";
import {useNavigate} from "react-router-dom"; // Import constant to check if the user type is supplier

// Main component for handling contractor and supplier restriction updates based on userType
const ContractorRestrictionApp = ({userType}) => {
    // useTranslation hook for multi-language support
    const { t } = useTranslation();
    const navigate = useNavigate();
    const DEV = useSelector((state) => state?.CommonsSlice?.development); // Get development flag from Redux state
    const { permission } = useSelector((state) => state.authenticatioauthennSlice); // Get user permissions from Redux state
    // useDispatch hook to dispatch actions to Redux store
    const dispatch = useDispatch();

    // useState hooks to manage local state for waitTime and form validation (empty fields)
    const [waitTime, setWaitTime] = useState(0); // Stores the wait time value
    const [emptyFields, setEmptyFields] = useState(false); // Flag to check if required fields are empty

    // Accessing data from the Redux store via useSelector for contractor and supplier restriction information
    const { getSupplierRestriction, getContractorRestriction, updateContractorRestriction, updateSupplierRestriction } =
        useSelector((state) => state.RestrictionSlice);


    // Function to handle the update of restriction information based on the userType
    const handleUpdateRestriction = () => {
        // Check if the waitTime is empty and show an error message if true
        if(waitTime === ""){
            setEmptyFields(true); // Set emptyFields flag to true if no waitTime is provided
            return; // Exit the function early if fields are empty
        }

        setEmptyFields(false); // Reset emptyFields flag if the waitTime is valid

        // If userType is supplier, prepare and dispatch UpdateSupplierRestriction action
        if (userType === supplierFile) {
            const data = {
                id: getSupplierRestriction?.id, // Get supplier restriction ID from Redux store
                waitTime, // The updated waitTime value
            };
            dispatch(UpdateSupplierRestriction(data)); // Dispatch the update action for supplier restrictions
        } else { // If userType is not supplier (assumed to be contractor), update contractor restriction
            const data = {
                id: getContractorRestriction?.id, // Get contractor restriction ID from Redux store
                waitTime, // The updated waitTime value
            };
            dispatch(UpdateContractorRestriction(data)); // Dispatch the update action for contractor restrictions
        }
    };

    // useEffect to dispatch the appropriate action when the component mounts or the update actions change
    useEffect(() => {
        if (userType === supplierFile) {
            dispatch(GetSupplierRestriction()); // Dispatch action to get supplier restriction data if userType is supplier
        } else {
            dispatch(GetContractorRestriction()); // Dispatch action to get contractor restriction data if userType is contractor
        }
    }, [updateContractorRestriction, updateSupplierRestriction]); // Dependency array to re-fetch data when update actions change

    // useEffect to update the local state (waitTime) when the userType changes or the restriction data is updated
    useEffect(() => {
        // If userType is supplier, set the waitTime from supplier restriction data
        if (userType === supplierFile) {
            setWaitTime(getSupplierRestriction?.waitTime); // Update the waitTime state with the supplier restriction data
        } else { // If userType is contractor, set the waitTime from contractor restriction data
            setWaitTime(getContractorRestriction?.waitTime); // Update the waitTime state with the contractor restriction data
        }
    }, [userType === supplierFile ? getSupplierRestriction?.id : getContractorRestriction?.id]); // Only run when the restriction ID changes

    // Return the necessary values and functions for the component
    return {
        t, // Translation function for i18n support
        navigate,
        DEV,
        permission,
        emptyFields, // Flag to check if fields are empty
        waitTime, // The waitTime value from state
        setWaitTime, // Function to update the waitTime state
        handleUpdateRestriction, // Function to handle restriction update
    };
}

export default ContractorRestrictionApp; // Export the component for use in other parts of the application
